import { useState, useEffect } from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

export default function CareersPage() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    certifications: [] as string[],
    availability: '',
    relocate: '',
    resume: '',
    coverLetter: '',
    referral: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const { ref: benefitsRef, isVisible: benefitsVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: formRef, isVisible: formVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: openingsRef, isVisible: openingsVisible } = useScrollAnimation({ threshold: 0.1 });

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCertificationChange = (cert: string) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.includes(cert)
        ? prev.certifications.filter(c => c !== cert)
        : [...prev.certifications, cert]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const submitData = new URLSearchParams();
    submitData.append('firstName', formData.firstName);
    submitData.append('lastName', formData.lastName);
    submitData.append('email', formData.email);
    submitData.append('phone', formData.phone);
    submitData.append('position', formData.position);
    submitData.append('experience', formData.experience);
    submitData.append('certifications', formData.certifications.join(', '));
    submitData.append('availability', formData.availability);
    submitData.append('relocate', formData.relocate);
    submitData.append('coverLetter', formData.coverLetter);
    submitData.append('referral', formData.referral);

    try {
      await fetch('https://readdy.ai/api/form/d63sufpov5qgbon323h0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: submitData.toString(),
      });
      setSubmitSuccess(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        position: '',
        experience: '',
        certifications: [],
        availability: '',
        relocate: '',
        resume: '',
        coverLetter: '',
        referral: ''
      });
    } catch (error) {
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const certificationOptions = [
    'ARRT (MR)',
    'ARRT (CT)',
    'ARRT (R)',
    'RDMS',
    'RVT',
    'CRT',
    'BLS/CPR',
    'ACLS'
  ];

  const openPositions = [
    {
      title: 'Mobile MRI Technologist',
      location: 'Bay Area, CA',
      type: 'Full-Time',
      description: 'Operate mobile MRI units across healthcare facilities in the Bay Area region.'
    },
    {
      title: 'CT Technologist',
      location: 'Los Angeles, CA',
      type: 'Full-Time',
      description: 'Perform CT scans in mobile units serving hospitals and clinics throughout LA County.'
    },
    {
      title: 'Ultrasound Technologist',
      location: 'Sacramento, CA',
      type: 'Per Diem',
      description: 'Provide mobile ultrasound services to healthcare facilities in the Sacramento region.'
    },
    {
      title: 'Radiologic Technologist',
      location: 'San Diego, CA',
      type: 'Full-Time',
      description: 'Deliver mobile X-ray services to skilled nursing facilities and hospitals.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAFBFC]">
      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 sm:pt-32 sm:pb-24 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://public.readdy.ai/ai/img_res/8752101a632ca6291c6c85fb83d4d386.jpg)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy/95 via-navy/85 to-navy/70"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6">
          <div className="max-w-2xl">
            <span 
              className={`inline-block text-teal text-sm font-medium tracking-wider uppercase mb-4 transition-all duration-700 ${
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              Join Our Team
            </span>
            <h1 
              className={`text-white text-3xl sm:text-4xl lg:text-5xl font-light leading-tight mb-6 transition-all duration-700 delay-100 ${
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              Build Your Career in Mobile Medical Imaging
            </h1>
            <p 
              className={`text-white/80 text-base sm:text-lg leading-relaxed mb-8 transition-all duration-700 delay-200 ${
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              Join California's leading mobile imaging provider. We offer competitive compensation, flexible schedules, and the opportunity to make a real difference in patient care.
            </p>
            <div 
              className={`flex flex-wrap gap-4 transition-all duration-700 delay-300 ${
                isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <a 
                href="#apply" 
                className="bg-teal text-white px-8 py-3.5 rounded-lg font-medium hover:bg-teal/90 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                Apply Now
              </a>
              <a 
                href="#openings" 
                className="bg-white/10 text-white px-8 py-3.5 rounded-lg font-medium border border-white/30 hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                View Openings
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section ref={benefitsRef} className="py-16 sm:py-24 px-5 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 
              className={`text-navy text-2xl sm:text-3xl lg:text-4xl font-light mb-4 transition-all duration-700 ${
                benefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              Why Work at Mediline?
            </h2>
            <p 
              className={`text-navy/60 text-base sm:text-lg max-w-2xl mx-auto transition-all duration-700 delay-100 ${
                benefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              We invest in our technologists because they're the heart of our service
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: 'ri-money-dollar-circle-line', title: 'Competitive Pay', desc: 'Industry-leading compensation with performance bonuses' },
              { icon: 'ri-calendar-check-line', title: 'Flexible Schedules', desc: 'Full-time, part-time, and per diem options available' },
              { icon: 'ri-heart-pulse-line', title: 'Full Benefits', desc: 'Medical, dental, vision, and 401(k) matching' },
              { icon: 'ri-graduation-cap-line', title: 'CE Credits', desc: 'Paid continuing education and certification support' },
              { icon: 'ri-car-line', title: 'Travel Stipends', desc: 'Mileage reimbursement and travel allowances' },
              { icon: 'ri-team-line', title: 'Supportive Team', desc: 'Collaborative environment with experienced leadership' },
              { icon: 'ri-line-chart-line', title: 'Career Growth', desc: 'Clear advancement paths and leadership opportunities' },
              { icon: 'ri-shield-star-line', title: 'Job Security', desc: 'Stable employment with California\'s top provider' }
            ].map((benefit, index) => (
              <div 
                key={index}
                className={`bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-500 group ${
                  benefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${150 + index * 50}ms` }}
              >
                <div className="w-12 h-12 bg-teal/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-teal/20 transition-colors duration-300">
                  <i className={`${benefit.icon} text-teal text-2xl`}></i>
                </div>
                <h3 className="text-navy font-medium text-lg mb-2">{benefit.title}</h3>
                <p className="text-navy/60 text-sm leading-relaxed">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions Section */}
      <section ref={openingsRef} id="openings" className="py-16 sm:py-24 px-5 sm:px-6 bg-navy/[0.02]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 
              className={`text-navy text-2xl sm:text-3xl lg:text-4xl font-light mb-4 transition-all duration-700 ${
                openingsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              Current Openings
            </h2>
            <p 
              className={`text-navy/60 text-base sm:text-lg max-w-2xl mx-auto transition-all duration-700 delay-100 ${
                openingsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              Find the perfect position that matches your skills and career goals
            </p>
          </div>

          <div className="grid gap-4 sm:gap-6">
            {openPositions.map((position, index) => (
              <div 
                key={index}
                className={`bg-white rounded-xl p-6 sm:p-8 shadow-sm hover:shadow-md transition-all duration-500 group cursor-pointer ${
                  openingsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${150 + index * 100}ms` }}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <h3 className="text-navy font-medium text-lg sm:text-xl">{position.title}</h3>
                      <span className="bg-teal/10 text-teal text-xs font-medium px-3 py-1 rounded-full">
                        {position.type}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-navy/50 text-sm mb-3">
                      <i className="ri-map-pin-line"></i>
                      <span>{position.location}</span>
                    </div>
                    <p className="text-navy/60 text-sm leading-relaxed">{position.description}</p>
                  </div>
                  <a 
                    href="#apply"
                    className="flex items-center gap-2 text-teal font-medium text-sm hover:gap-3 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    Apply Now
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      <section ref={formRef} id="apply" className="py-16 sm:py-24 px-5 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className={`text-navy text-2xl sm:text-3xl lg:text-4xl font-light mb-4 transition-all duration-700 ${
                formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              Apply Today
            </h2>
            <p 
              className={`text-navy/60 text-base sm:text-lg max-w-2xl mx-auto transition-all duration-700 delay-100 ${
                formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              Complete the form below and our recruitment team will contact you within 48 hours
            </p>
          </div>

          {submitSuccess ? (
            <div 
              className={`bg-teal/10 border border-teal/30 rounded-xl p-8 sm:p-12 text-center transition-all duration-700 ${
                formVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
              }`}
            >
              <div className="w-16 h-16 bg-teal/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-check-line text-teal text-3xl"></i>
              </div>
              <h3 className="text-navy text-2xl font-medium mb-3">Application Submitted!</h3>
              <p className="text-navy/60 text-base mb-6">
                Thank you for your interest in joining Mediline. Our recruitment team will review your application and contact you within 48 hours.
              </p>
              <button 
                onClick={() => setSubmitSuccess(false)}
                className="text-teal font-medium hover:underline cursor-pointer"
              >
                Submit Another Application
              </button>
            </div>
          ) : (
            <form 
              id="job-application-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className={`bg-white rounded-2xl p-6 sm:p-10 shadow-sm transition-all duration-700 delay-200 ${
                formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              {/* Personal Information */}
              <div className="mb-8">
                <h3 className="text-navy font-medium text-lg mb-6 flex items-center gap-2">
                  <i className="ri-user-line text-teal"></i>
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-navy/70 text-sm font-medium mb-2">First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-[#F5F7FA] border border-transparent rounded-lg px-4 py-3 text-navy text-sm focus:outline-none focus:border-teal focus:bg-white transition-all duration-300"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block text-navy/70 text-sm font-medium mb-2">Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-[#F5F7FA] border border-transparent rounded-lg px-4 py-3 text-navy text-sm focus:outline-none focus:border-teal focus:bg-white transition-all duration-300"
                      placeholder="Smith"
                    />
                  </div>
                  <div>
                    <label className="block text-navy/70 text-sm font-medium mb-2">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-[#F5F7FA] border border-transparent rounded-lg px-4 py-3 text-navy text-sm focus:outline-none focus:border-teal focus:bg-white transition-all duration-300"
                      placeholder="john.smith@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-navy/70 text-sm font-medium mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-[#F5F7FA] border border-transparent rounded-lg px-4 py-3 text-navy text-sm focus:outline-none focus:border-teal focus:bg-white transition-all duration-300"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
              </div>

              {/* Position Details */}
              <div className="mb-8">
                <h3 className="text-navy font-medium text-lg mb-6 flex items-center gap-2">
                  <i className="ri-briefcase-line text-teal"></i>
                  Position Details
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-navy/70 text-sm font-medium mb-2">Position Applying For *</label>
                    <select
                      name="position"
                      value={formData.position}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-[#F5F7FA] border border-transparent rounded-lg px-4 py-3 text-navy text-sm focus:outline-none focus:border-teal focus:bg-white transition-all duration-300 cursor-pointer"
                    >
                      <option value="">Select a position</option>
                      <option value="MRI Technologist">MRI Technologist</option>
                      <option value="CT Technologist">CT Technologist</option>
                      <option value="Ultrasound Technologist">Ultrasound Technologist</option>
                      <option value="Radiologic Technologist">Radiologic Technologist</option>
                      <option value="Multi-Modality Technologist">Multi-Modality Technologist</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-navy/70 text-sm font-medium mb-2">Years of Experience *</label>
                    <select
                      name="experience"
                      value={formData.experience}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-[#F5F7FA] border border-transparent rounded-lg px-4 py-3 text-navy text-sm focus:outline-none focus:border-teal focus:bg-white transition-all duration-300 cursor-pointer"
                    >
                      <option value="">Select experience level</option>
                      <option value="0-1 years">0-1 years</option>
                      <option value="1-3 years">1-3 years</option>
                      <option value="3-5 years">3-5 years</option>
                      <option value="5-10 years">5-10 years</option>
                      <option value="10+ years">10+ years</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Certifications */}
              <div className="mb-8">
                <h3 className="text-navy font-medium text-lg mb-6 flex items-center gap-2">
                  <i className="ri-award-line text-teal"></i>
                  Certifications
                </h3>
                <p className="text-navy/60 text-sm mb-4">Select all certifications you currently hold:</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {certificationOptions.map((cert, index) => (
                    <label 
                      key={index}
                      className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all duration-300 ${
                        formData.certifications.includes(cert)
                          ? 'bg-teal/10 border-teal text-navy'
                          : 'bg-[#F5F7FA] border-transparent text-navy/70 hover:border-navy/20'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.certifications.includes(cert)}
                        onChange={() => handleCertificationChange(cert)}
                        className="sr-only"
                      />
                      <div className={`w-4 h-4 rounded border flex items-center justify-center transition-all duration-300 ${
                        formData.certifications.includes(cert)
                          ? 'bg-teal border-teal'
                          : 'border-navy/30'
                      }`}>
                        {formData.certifications.includes(cert) && (
                          <i className="ri-check-line text-white text-xs"></i>
                        )}
                      </div>
                      <span className="text-sm font-medium">{cert}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Availability */}
              <div className="mb-8">
                <h3 className="text-navy font-medium text-lg mb-6 flex items-center gap-2">
                  <i className="ri-time-line text-teal"></i>
                  Availability
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-navy/70 text-sm font-medium mb-2">Preferred Schedule *</label>
                    <select
                      name="availability"
                      value={formData.availability}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-[#F5F7FA] border border-transparent rounded-lg px-4 py-3 text-navy text-sm focus:outline-none focus:border-teal focus:bg-white transition-all duration-300 cursor-pointer"
                    >
                      <option value="">Select availability</option>
                      <option value="Full-Time">Full-Time</option>
                      <option value="Part-Time">Part-Time</option>
                      <option value="Per Diem">Per Diem</option>
                      <option value="Flexible">Flexible / Open to All</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-navy/70 text-sm font-medium mb-2">Willing to Relocate? *</label>
                    <select
                      name="relocate"
                      value={formData.relocate}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-[#F5F7FA] border border-transparent rounded-lg px-4 py-3 text-navy text-sm focus:outline-none focus:border-teal focus:bg-white transition-all duration-300 cursor-pointer"
                    >
                      <option value="">Select an option</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                      <option value="Depends on Location">Depends on Location</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div className="mb-8">
                <h3 className="text-navy font-medium text-lg mb-6 flex items-center gap-2">
                  <i className="ri-file-text-line text-teal"></i>
                  Additional Information
                </h3>
                <div className="space-y-4 sm:space-y-6">
                  <div>
                    <label className="block text-navy/70 text-sm font-medium mb-2">Cover Letter / Why Mediline?</label>
                    <textarea
                      name="coverLetter"
                      value={formData.coverLetter}
                      onChange={handleInputChange}
                      maxLength={500}
                      rows={4}
                      className="w-full bg-[#F5F7FA] border border-transparent rounded-lg px-4 py-3 text-navy text-sm focus:outline-none focus:border-teal focus:bg-white transition-all duration-300 resize-none"
                      placeholder="Tell us about yourself and why you'd like to join Mediline..."
                    />
                    <p className="text-navy/40 text-xs mt-1 text-right">{formData.coverLetter.length}/500 characters</p>
                  </div>
                  <div>
                    <label className="block text-navy/70 text-sm font-medium mb-2">How did you hear about us?</label>
                    <select
                      name="referral"
                      value={formData.referral}
                      onChange={handleInputChange}
                      className="w-full bg-[#F5F7FA] border border-transparent rounded-lg px-4 py-3 text-navy text-sm focus:outline-none focus:border-teal focus:bg-white transition-all duration-300 cursor-pointer"
                    >
                      <option value="">Select an option</option>
                      <option value="Indeed">Indeed</option>
                      <option value="LinkedIn">LinkedIn</option>
                      <option value="Company Website">Company Website</option>
                      <option value="Employee Referral">Employee Referral</option>
                      <option value="Job Fair">Job Fair</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-teal text-white py-4 rounded-lg font-medium text-base hover:bg-teal/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Submitting Application...
                    </>
                  ) : (
                    <>
                      Submit Application
                      <i className="ri-arrow-right-line"></i>
                    </>
                  )}
                </button>
                <p className="text-navy/50 text-xs text-center mt-4">
                  By submitting this application, you agree to our privacy policy and consent to be contacted regarding employment opportunities.
                </p>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 px-5 sm:px-6 bg-navy">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-white text-2xl sm:text-3xl lg:text-4xl font-light mb-4">
            Have Questions About Careers?
          </h2>
          <p className="text-white/70 text-base sm:text-lg mb-8 max-w-2xl mx-auto">
            Our recruitment team is here to help. Reach out to learn more about opportunities at Mediline.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a 
              href="mailto:admin@medilinemedical.com"
              className="flex items-center gap-2 bg-white text-navy px-8 py-3.5 rounded-lg font-medium hover:bg-white/95 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line"></i>
              admin@medilinemedical.com
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
